import { useAuth0 } from '@auth0/auth0-react';
import { subject } from '@casl/ability';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
    faEnvelope,
    faNewspaper,
} from '@fortawesome/pro-light-svg-icons';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Grid from '@mui/material/Grid2';
import { useTheme } from '@mui/material/styles';
import useMediaQuery from '@mui/material/useMediaQuery';
import axios from 'axios';
import classNames from 'classnames';
import { DateTime } from 'luxon';
import React, {
    useCallback,
    useContext,
    useEffect,
    useRef,
    useState,
} from 'react';
import {
    AdSlot,
    DFPManager,
    DFPSlotsProvider,
} from 'react-dfp';
import {
    useLocation,
    useNavigate,
    useParams,
} from 'react-router-dom';
import { toast } from 'react-toastify';

import {
    getAuthToken,
    loadListingAbilitiesForUser,
} from '../../auth/authHelpers';
import { AbilityContext } from '../../auth/Can';
import { Descriptions } from '../../constants/Descriptions';
import { LISTING_TYPE_ORGANIZATION } from '../../constants/site';
import {
    COLORS,
    MCO_THEME,
    POL_THEME,
    ThemeContext,
} from '../../constants/theme';
import { Titles } from '../../constants/titles';
import { AppContext } from '../../context/AppContext';
import customerDataRequest, {
    customerDataMicroserviceRequest,
} from '../../services/api/customer-data';
import {
    getBillingDataFromListing,
    getContactDataFromListing,
    getSocialDataFromListing,
} from '../../services/listing';
import {
    loadContractsCallback,
    loadOrganizationCallback,
    loadPublicationsCallback,
} from '../../utils/organization';
import { getPublicationPage } from '../../utils/routes';

import MassTimesSummary from '../mass-times/MassTimeSummary';
import PopupSubscribeForm from '../popup-subscribe-form/PopupSubscribeForm';
import AboutUs from '../shared/AboutUs';
import ContactInformation from '../shared/ContactInformation';
import DetailPageHeader from '../shared/DetailPageHeader';
import Events from '../shared/Events/Events';
import EmailUnsubscribeForm from '../shared/Forms/EmailUnsubscribe/EmailUnsubscribeForm';
import PublicationsSubscribeForm from '../shared/Forms/PublicationsSubscribe';
import Gallery from '../shared/Gallery';
import ListingSocialDisplay from '../shared/ListingSocialDisplay';
import Loader from '../shared/Loader';
import ModalDialog from '../shared/ModalDialog/Base';
import OwnershipRequest from '../shared/OwnershipRequest';
import Videos from '../shared/Videos/Videos';

import AdminInfo from './AdminInfo/AdminInfo';
import AdsSponsorsSection from './AdsSponsorsSection';
import ReadingsSection from './ReadingsSection';
import RecentPublicationsSection from './RecentPublicationsSection';
import ViewBulletinSection from './ViewBulletinSection';

import styles from './OrganizationDetailContainer.scss';

const OrganizationDetailContainer = () => {
    const theme = useTheme();
    const isExtraSmallScreen = useMediaQuery(theme.breakpoints.down('sm'));
    const isMediumScreen = useMediaQuery(theme.breakpoints.up('md'));
    const isLargeScreen = useMediaQuery(theme.breakpoints.up('lg'));
    const isExtraLargeScreen = useMediaQuery(theme.breakpoints.up('xl'));

    const themeContext = useContext(ThemeContext);
    const { setPageTitle, setPageDescription } = useContext(AppContext);

    const location = useLocation();
    const { slug, variant } = useParams();

    const openSubscriptionForm = location.hash.toLowerCase() === '#subscribe';

    const contextAbility = useContext(AbilityContext);
    const [ability, setAbility] = useState({});

    const {
        getAccessTokenSilently,
        user,
        isAuthenticated,
    } = useAuth0();

    // State for subscribe popup window
    const [isPopupOpen, setIsPopupOpen] = useState(false);
    const [readings, setReadings] = useState({});
    const [organization, setOrganization] = useState({});
    const [organizationContractIds, setOrganizationContractIds] = useState(undefined);
    const { salesforce_id: salesforceId } = organization || {};
    const [socialData, setSocialData] = useState({});
    const [contactInfo, setContactInfo] = useState({});
    const [billingInfo, setBillingInfo] = useState({});
    const history = useNavigate();

    const [publications, setPublications] = useState([]);
    const [arePublicationsLoading, setArePublicationsLoading] = useState(false);
    const [galleryPhotos, setGalleryPhotos] = useState([]);
    const [events, setEvents] = useState([]);
    const [eventsLoading, setEventsLoading] = useState(false);
    const [dataReady, setDataReady] = useState({ org: false, contracts: false });
    const [subscribeFormTitle, setSubscribeFormTitle] = useState('Subscribe');
    const adSetupComplete = useRef(false);
    const eventsRef = useRef(null);

    const [openSubscriberModal, setOpenSubscriberModal] = useState(openSubscriptionForm);

    const [selectedPublication, setSelectedPublication] = useState(null);
    const [viewerPublication, setViewerPublication] = useState(null);
    const [isHighResolutionThumbnail, setIsHighResolutionThumbnail] = useState(false);

    const [isFormSubmitted, setIsFormSubmitted] = useState(false);
    const [isSaving, setIsSaving] = useState(false);
    // Variant set by URL pathname (for /subscribe and /unsubscribe)
    const [currentVariant, setCurrentVariant] = useState('default');
    // Set slot for right column filler ad
    const [areFillerAdsSwapped, setAreFillerAdsSwapped] = useState(false);
    const [isBannerAdPresent, setIsBannerAdPresent] = useState(false);

    const canAdminClaimed = useCallback(() => {
        if (!Object.keys(ability).length) {
            return false;
        }

        return ability.can('admin', subject('claimed', { id: salesforceId }));
    }, [
        ability,
        salesforceId,
    ]);

    const loadListingAbilities = useCallback(async () => {
        const token = isAuthenticated ? await getAuthToken(getAccessTokenSilently) : null;
        setAbility(await loadListingAbilitiesForUser(user, token, contextAbility));
    }, [isAuthenticated, getAccessTokenSilently, user, contextAbility]);

    const organizationAds = {
        SAF2_Listing_Sponsor_160x600: {
            unit: 'SAF2_Listing_Sponsor_160x600',
            sizes: [[160, 600]],
        },
        SAF2_Listing_Sponsor_320x50_Mobile: {
            unit: 'SAF2_Listing_Sponsor_320x50_Mobile',
            sizes: [[320, 50]],
        },
        SAF2_Listing_Sponsor_300x100_Mobile: {
            unit: 'SAF2_Listing_Sponsor_300x100_Mobile',
            sizes: [[300, 100]],
        },
        SAF2_Listing_Sponsor_300x50_Mobile: {
            unit: 'SAF2_Listing_Sponsor_300x50_Mobile',
            sizes: [[300, 50]],
        },
        SAF2_OrgListing1_300x50_Mobile: {
            unit: 'SAF2_OrgListing1_300x50_Mobile',
            sizes: [[300, 50]],
        },
        SAF2_Listing_Sponsor_970x250: {
            unit: 'SAF2_Listing_Sponsor_970x250',
            sizes: [[970, 250]],
        },
        OSC2_Listing_Sponsor_300x100_Mobile: {
            unit: 'OSC2_Listing_Sponsor_300x100_Mobile',
            sizes: [[300, 100]],
        },
        OSC2_Listing_Sponsor_300x50_Mobile: {
            unit: 'OSC2_Listing_Sponsor_300x50_Mobile',
            sizes: [[300, 50]],
        },
        OSC2_OrgListing1_300x50_Mobile: {
            unit: 'OSC2_OrgListing1_300x50_Mobile',
            sizes: [[300, 50]],
        },
        OSC2_Listing_Sponsor_320x50_Mobile: {
            unit: 'OSC2_Listing_Sponsor_320x50_Mobile',
            sizes: [[320, 50]],
        },
        OSC2_Listing_Sponsor_970x250: {
            unit: 'OSC2_Listing_Sponsor_970x250',
            sizes: [[970, 250]],
        },
        OSC2_Listing_Sponsor_160x600: {
            unit: 'OSC2_Listing_Sponsor_160x600',
            sizes: [[160, 600]],
        },
    };

    const rightColumnAdSlots = {
        POL: [
            {
                unit: 'SAF2_Listing_Sponsor_300x600',
                sizes: [[300, 600]],
            },
            {
                unit: 'SAF2_OrgListing1_300x250',
                sizes: [[300, 250]],
            },
            {
                unit: 'SAF2_ListingInternal1_300x150',
                sizes: [[300, 150]],
            },
            {
                unit: 'SAF2_OrgListing2_300x100',
                sizes: [[300, 100]],
            },
            {
                unit: 'SAF2_OrgListing3_300x250',
                sizes: [[300, 250]],
            },
            {
                unit: 'SAF2_OrgListing4_300x100',
                sizes: [[300, 100]],
            },
        ],
        MCO: [
            {
                unit: 'OSC2_ListingSponsor_300x600',
                sizes: [[300, 600]],
            },
            {
                unit: 'OSC2_OrgListing1_300x250',
                sizes: [[300, 250]],
            },
            {
                unit: 'OSC2_ListingInternal1_300x150',
                sizes: [[300, 150]],
            },
            {
                unit: 'OSC2_OrgListing2_300x100',
                sizes: [[300, 100]],
            },
            {
                unit: 'OSC2_OrgListing3_300x250',
                sizes: [[300, 250]],
            },
            {
                unit: 'OSC2_OrgListing4_300x100',
                sizes: [[300, 100]],
            },
        ],
    };

    const loadPublications = useCallback(async () => {
        setArePublicationsLoading(true);
        if (salesforceId) {
            const orgType = themeContext === POL_THEME ? 'Church' : 'Community';
            await loadPublicationsCallback(salesforceId, orgType, setPublications, setArePublicationsLoading);
        }
    }, [salesforceId, themeContext]);

    const loadContracts = useCallback(() => {
        if (salesforceId) {
            loadContractsCallback(salesforceId, getAccessTokenSilently, () => null, setOrganizationContractIds);
        }
    }, [salesforceId, getAccessTokenSilently]);

    const loadEvents = useCallback(async () => {
        setEventsLoading(true);
        try {
            if (salesforceId) {
                const response = await customerDataMicroserviceRequest('get', process.env.EVENT_MANAGEMENT_API_URL, `/organizations/${salesforceId}/events`);
                setEvents(response.data);
            }
        } catch {
            toast.error('There was an error loading events. Please try again in a few minutes.');
        }
        setEventsLoading(false);
    }, [salesforceId]);

    const setOrganizationData = (newOrganization) => {
        setOrganization(newOrganization);
        setSocialData(getSocialDataFromListing(newOrganization));
        setContactInfo(getContactDataFromListing(newOrganization));
        setBillingInfo(getBillingDataFromListing(newOrganization));
    };

    const loadOrganization = useCallback((slugToLoad = slug) => {
        loadOrganizationCallback(slugToLoad, () => null, setOrganizationData, history, themeContext);
    }, [slug, history, themeContext]);

    const getStartOfWeek = () => {
        // Grab today's date to calculate where we are in the week
        const today = DateTime.now();
        // Reduce start of week by 1 day to go from Monday ==> Sunday
        let startOfWeek = today.startOf('week').minus({ days: 1 });
        // Check if start of week is 7 days behind current date so we know if it is the following Sunday
        const isFollowingSunday = today.day - startOfWeek.day === 7;
        // If it is the following Sunday
        if (isFollowingSunday) {
            // increase start of week 6 days instead of reducing 1 day
            startOfWeek = today.startOf('week').plus({ days: 6 });
        }
        return startOfWeek;
    };
    const loadReadings = useCallback(() => {
        const date = getStartOfWeek().toISODate();

        customerDataMicroserviceRequest('get', process.env.WEEKLY_READINGS_API_URL, `/weekly-readings/startDate/${date}`)
            .then((res) => {
                setReadings(res.data);
            })
            .catch(() => {
                toast.error('There was an error loading Weekly Readings. Please try again in a few minutes.');
            });
    }, []);

    const loadGalleryPhotos = useCallback(() => {
        if (salesforceId) {
            customerDataMicroserviceRequest('get', process.env.PHOTO_MANAGEMENT_API_URL, `/organizations/${salesforceId}/photos/?limit=50`).then((res) => {
                setGalleryPhotos(res.data);
            }).catch(() => {
                toast.error('There was an error loading images. Please try again in a few minutes.');
            });
        }
    }, [salesforceId]);

    const sortedPublications = publications.sort((publication1, publication2) => {
        const publication1Date = new Date(publication1.publishDate);
        const publication2Date = new Date(publication2.publishDate);

        // If different date, sort to show newest publication first
        if (publication1Date - publication2Date !== 0) {
            return publication2Date - publication1Date;
        }

        // If dates are equal, compare by name alphabetically
        return publication1.name.localeCompare(publication2.name);
    });

    useEffect(() => {
        loadOrganization();
    }, [loadOrganization]);

    useEffect(() => {
        if (organization && organization.salesforce_id) {
            setDataReady((prev) => ({ ...prev, org: true }));
        }
    }, [organization]);

    useEffect(() => {
        loadPublications();
    }, [loadPublications]);

    useEffect(() => {
        loadContracts();
    }, [loadContracts]);

    useEffect(() => {
        if (organizationContractIds || organizationContractIds === null) {
            setDataReady((prev) => ({ ...prev, contracts: true }));
        }
    }, [organizationContractIds]);

    useEffect(() => {
        loadReadings();
    }, [loadReadings]);

    useEffect(() => {
        loadGalleryPhotos();
    }, [loadGalleryPhotos]);

    useEffect(() => {
        loadEvents();
    }, [loadEvents]);

    useEffect(() => {
        if (sortedPublications && sortedPublications.length) {
            setSelectedPublication(sortedPublications[0]);
            setIsHighResolutionThumbnail(!!sortedPublications[0].highResolutionThumbnailUrl);
        }
    }, [sortedPublications]);

    // useEffect for subscribe popup banner
    useEffect(() => {
        // Check if org is a publication customer to set subscribe popup state
        // And check if this is first visit to Org page for subscribe popup state
        if (
            organization.is_publication_customer
            && localStorage.getItem(`${salesforceId}-popup-shown`) === null
            && salesforceId
        ) {
            setIsPopupOpen(true);
            // If local storage item didn't exist, create it
            localStorage.setItem(`${salesforceId}-popup-shown`, true);
        }
    }, [organization, salesforceId]);

    // useEffect for setting variant if URL location changes
    useEffect(() => {
        if (variant === 'subscribe' || variant === 'unsubscribe') {
            setCurrentVariant(variant);
        } else {
            setCurrentVariant('default');
        }

        const { search } = location;
        const searchParams = new URLSearchParams(search);
        const eventScroll = searchParams.get('events');

        if (eventScroll && eventsRef.current) {
            eventsRef.current.scrollIntoView();
        }
    }, [location, variant]);

    useEffect(() => {
        if (dataReady.org && dataReady.contracts && !adSetupComplete.current) {
            DFPManager.setTargetingArguments({
                Church_ID: organizationContractIds || '',
                Salesforce_Id: salesforceId,
                City: organization.physical_city,
                State: organization.physical_state,
                Zip: organization.physical_postal_code,
                Category: organization.tags,
            });
            DFPManager.refresh();
            adSetupComplete.current = true;
        }
    }, [dataReady, organization, organizationContractIds, salesforceId]);

    useEffect(() => {
        loadListingAbilities();
    }, [loadListingAbilities]);

    useEffect(() => {
        if (organization && organization.physical_title) {
            if (themeContext === MCO_THEME) {
                switch (currentVariant) {
                    case 'subscribe':
                        setPageTitle(Titles.MCO_SUBSCRIBE(organization));
                        setPageDescription(Descriptions.MCO_SUBSCRIBE(organization));
                        break;
                    case 'unsubscribe':
                        setPageTitle(Titles.MCO_UNSUBSCRIBE(organization));
                        setPageDescription(Descriptions.MCO_UNSUBSCRIBE(organization));
                        break;
                    default:
                        setPageTitle(Titles.MCO_ORGANIZATION(organization));
                        setPageDescription(Descriptions.MCO_ORGANIZATION(organization));
                }
            } else if (themeContext === POL_THEME) {
                switch (currentVariant) {
                    case 'subscribe':
                        setPageTitle(Titles.POL_SUBSCRIBE(organization));
                        setPageDescription(Descriptions.POL_SUBSCRIBE(organization));
                        break;
                    case 'unsubscribe':
                        setPageTitle(Titles.POL_UNSUBSCRIBE(organization));
                        setPageDescription(Descriptions.POL_UNSUBSCRIBE(organization));
                        break;
                    default:
                        setPageTitle(Titles.POL_ORGANIZATION(organization));
                        setPageDescription(Descriptions.POL_ORGANIZATION(organization));
                }
            }
        }
    }, [
        organization,
        themeContext,
        currentVariant,
        setPageTitle,
        setPageDescription,
    ]);

    useEffect(() => {
        if (isFormSubmitted) {
            setSubscribeFormTitle('Subscription Confirmation');
        }
    }, [isFormSubmitted]);

    // External subscribe form
    const onSubmitSubscribeForm = (values) => {
        setIsSaving(true);
        customerDataRequest('post', `/organizations/${salesforceId}/subscribe/`, null, {
            salesforceId,
            fName: values.firstName,
            lName: values.lastName,
            email: values.contactEmail,
            subscriptionSource: 'web',
        })
            .then(() => {
                setIsSaving(false);
                setIsFormSubmitted(true);
            })
            .catch(() => {
                setIsSaving(false);
                toast.error('There was an error subscribing');
            });
    };

    // External unsubscribe form
    const onSubmitUnsubscribeForm = useCallback((values) => {
        setIsSaving(true);
        const url = process.env.SENDGRID_INTEGRATION_SERVICE;

        axios.patch(`${url}/unsubscribe`, {
            emailAddress: values.contactEmail,
            salesforceID: salesforceId,
        }, {
            headers: {
                'Content-type': 'application/json',
            },
        })
            .then(() => {
                setIsSaving(false);
                setIsFormSubmitted(true);
            })
            .catch(() => {
                setIsSaving(false);
                toast.error('There was an error unsubscribing');
            });
    }, [salesforceId]);

    const onCancelSubmitForm = () => {
        setIsFormSubmitted(false);
        history(`/organization/${slug}`);
    };

    // Subscribe button above publication
    const onSubmitPublicationSubscribeForm = async (values) => {
        setIsSaving(true);
        await customerDataRequest('post', `/organizations/${salesforceId}/subscribe/`, null, {
            salesforceId,
            fName: values.firstName,
            lName: values.lastName,
            email: values.contactEmail,
            subscriptionSource: 'web',
        })
            .then(() => {
                setIsSaving(false);
                setIsFormSubmitted(true);
            })
            .catch(() => {
                setIsSaving(false);
                toast.error('There was an error subscribing');
            });
    };

    const getSubscribeButtonText = () => {
        if (isExtraLargeScreen) {
            return `Receive ${themeContext === POL_THEME ? 'Bulletin' : 'Publication'} via Email`;
        }
        return 'Subscribe';
    };

    const handleCancel = () => {
        setIsFormSubmitted(false);
        setOpenSubscriberModal(false);
        setSubscribeFormTitle('Subscribe');
    };

    const TODAY = new Date().setHours(0, 0, 0);
    const hasActiveEvents = (eventList) => eventList && eventList.length > 0 && eventList.some((event) => !event.deleted_at && new Date(event.event_date) >= TODAY);

    const onViewPubClickHandler = () => {
        history(getPublicationPage(slug, selectedPublication.fileUrl));
    };

    const swapAdsOnSlotRender = (event) => {
        if (!event.event.isEmpty && isLargeScreen) {
            setAreFillerAdsSwapped(true);
        }
    };

    const checkIfBannerAdRenders = (event) => {
        if (!event.event.isEmpty) {
            setIsBannerAdPresent(true);
        }
    };

    const renderAdSlot = (ad, id, slotRenderHandler = () => { }) => (
        <AdSlot
            dfpNetworkId={process.env.DFP_NETWORK_ID}
            key={ad.unit}
            adUnit={ad.unit}
            sizes={ad.sizes}
            slotId={id}
            onSlotRender={(e) => slotRenderHandler(e)}
        />
    );

    const contactAndLatestPubContent = (
        <Grid
            container
            spacing={{
                xs: 3,
                md: 4,
                xl: 5,
            }}
            size={12}
        >
            <Grid
                className={styles.contactInfoAndLatestPubColumn}
                container
                direction={{
                    xs: 'column',
                    lg: isHighResolutionThumbnail ? 'column' : 'row',
                }}
                size={{
                    xs: 12,
                    md: 9,
                    lg: isHighResolutionThumbnail ? 5 : 12,
                    xl: isHighResolutionThumbnail ? 4.25 : 12,
                }}
                spacing={isHighResolutionThumbnail ? 1.5 : 2.5}
                wrap="nowrap"
            >
                <Grid
                    className={styles.alignContentStart}
                    container
                    size={{
                        xs: 12,
                        lg: isHighResolutionThumbnail ? 12 : 4.5,
                    }}
                >
                    <Grid
                        className={styles.contactInfoContainer}
                        size={12}
                    >
                        <ContactInformation
                            contactInfo={contactInfo}
                            websiteUrl={socialData.websiteUrl}
                            donationUrl={socialData.donationUrl}
                        />
                    </Grid>
                    {(
                        socialData.twitterUsername
                        || socialData.facebookUrl
                        || socialData.instagramUrl
                        || socialData.linkedinUrl
                        || socialData.youtubeUrl
                    ) && (
                        <Grid
                            className={styles.socialLinksContainer}
                            size={12}
                        >
                            <ListingSocialDisplay {...socialData} />
                        </Grid>
                    )}
                </Grid>
                <Grid
                    className={classNames(styles.massTimesContainer, !isHighResolutionThumbnail && isLargeScreen && styles.noMargin)}
                    size={{
                        xs: 12,
                        lg: isHighResolutionThumbnail ? 12 : 7.5,
                    }}
                    tabIndex={isLargeScreen ? 0 : -1}
                >
                    <MassTimesSummary
                        salesforceId={salesforceId}
                        organizationName={organization.physical_title || ''}
                        contactInfo={contactInfo}
                        latitude={organization.latitude}
                        longitude={organization.longitude}
                    />
                </Grid>
                <Grid
                    className={styles.mobileInlineAd}
                    size={12}
                    aria-hidden={!isExtraSmallScreen}
                >
                    {themeContext === POL_THEME
                        ? renderAdSlot(organizationAds.SAF2_Listing_Sponsor_300x100_Mobile, 'mobile-leaderboard-ad')
                        : renderAdSlot(organizationAds.OSC2_Listing_Sponsor_300x100_Mobile, 'mobile-leaderboard-ad')}
                </Grid>
            </Grid>
            {isHighResolutionThumbnail && (
                <Grid
                    className={styles.contactInfoAndLatestPubColumn}
                    container
                    size={{
                        xs: 12,
                        lg: 7,
                        xl: 7.75,
                    }}
                    spacing={{
                        xs: 2.5,
                        xl: 3,
                    }}
                >
                    <Grid
                        size={{
                            xs: 6,
                            xl: 5,
                        }}
                        sx={{
                            order: {
                                xs: 2,
                                sm: 1,
                            },
                        }}
                    >
                        <Button
                            className={styles.viewPubButton}
                            fullWidth
                            onClick={onViewPubClickHandler}
                            startIcon={<FontAwesomeIcon icon={faNewspaper} />}
                            variant="contained"
                        >
                            {themeContext === POL_THEME ? 'View Bulletin' : 'View Newsletter'}
                        </Button>
                    </Grid>
                    <Grid
                        size={{
                            xs: 6,
                            xl: 7,
                        }}
                        sx={{
                            order: {
                                xs: 3,
                                sm: 2,
                            },
                        }}
                    >
                        <Button
                            className={styles.subscribePubButton}
                            fullWidth
                            onClick={() => setOpenSubscriberModal(true)}
                            startIcon={<FontAwesomeIcon icon={faEnvelope} />}
                            variant="outlined"
                        >
                            {getSubscribeButtonText()}
                        </Button>
                        <ModalDialog
                            title={subscribeFormTitle}
                            content={(
                                <PublicationsSubscribeForm
                                    uniqueId="publicationsSubscribe"
                                    contactInfo={{
                                        firstName: '',
                                        lastName: '',
                                        contactEmail: '',
                                    }}
                                    organizationTitle={organization.physical_title}
                                    onClose={handleCancel}
                                    onSubmit={onSubmitPublicationSubscribeForm}
                                    isFormSubmitted={isFormSubmitted}
                                    isSaving={isSaving}
                                />
                            )}
                            open={openSubscriberModal}
                            onClose={handleCancel}
                            buttons={([])}
                        />
                    </Grid>
                    <Grid
                        size={12}
                        sx={{
                            order: {
                                xs: 1,
                                sm: 3,
                            },
                        }}
                    >
                        <ViewBulletinSection
                            slug={slug}
                            publication={selectedPublication}
                            setViewerPublication={setViewerPublication}
                        />
                    </Grid>
                </Grid>
            )}
        </Grid>
    );

    const subscribeFormContent = (
        <Grid size={12}>
            {currentVariant === 'subscribe' && (
                <PublicationsSubscribeForm
                    uniqueId="orgExternalSubscribe"
                    contactInfo={{
                        firstName: '',
                        lastName: '',
                        contactEmail: '',
                    }}
                    organizationTitle={organization.physical_title}
                    onSubmit={onSubmitSubscribeForm}
                    isFormSubmitted={isFormSubmitted}
                    isSaving={isSaving}
                    onClose={onCancelSubmitForm}
                />
            )}
            {currentVariant === 'unsubscribe' && (
                <EmailUnsubscribeForm
                    uniqueId="orgEmailUnsubscribe"
                    organizationTitle={organization.physical_title}
                    onSubmit={onSubmitUnsubscribeForm}
                    isFormSubmitted={isFormSubmitted}
                    isSaving={isSaving}
                    onClose={onCancelSubmitForm}
                />
            )}
        </Grid>
    );

    const adminMenuContent = (
        <AdminInfo
            org={organization}
            publicationsData={publications}
            orgContractIds={organizationContractIds}
            contactInfo={contactInfo}
            events={events}
            setEvents={setEvents}
            billingInfo={billingInfo}
            socialData={socialData}
            pageSlug={slug}
            loadOrganization={loadOrganization}
            arePublicationsLoading={arePublicationsLoading}
            setArePublicationsLoading={setArePublicationsLoading}
            loadPublications={loadPublications}
        />
    );

    const primaryContent = (
        <Grid
            className={classNames(styles.primaryContentContainer, styles.alignContentStart)}
            container
            spacing={3}
        >
            <Grid
                className={styles.topBannerAdContainer}
                size={12}
                aria-hidden={isExtraSmallScreen}
            >
                {themeContext === POL_THEME
                    ? renderAdSlot(organizationAds.SAF2_Listing_Sponsor_970x250, 'top-banner-ad', checkIfBannerAdRenders)
                    : renderAdSlot(organizationAds.OSC2_Listing_Sponsor_970x250, 'top-banner-ad', checkIfBannerAdRenders)}
            </Grid>
            {!isMediumScreen && organization && canAdminClaimed()
                && (
                    <Grid size={12}>
                        {adminMenuContent}
                    </Grid>
                )}
            <Grid size={12}>
                <DetailPageHeader
                    categories={organization.tags}
                    classes={{ root: styles.topDetailContainer }}
                    contactInfo={contactInfo}
                    pageSlug={slug}
                    pageTitle={organization.physical_title || ''}
                    listingType={LISTING_TYPE_ORGANIZATION}
                    currentVariant={currentVariant}
                    websiteUrl={socialData.websiteUrl}
                />
            </Grid>
            {/* render organization details or subscribe form  */}
            {currentVariant === 'default'
                ? contactAndLatestPubContent
                : subscribeFormContent}
            {!!sortedPublications.length && (
                <Grid size={12}>
                    <RecentPublicationsSection
                        publications={sortedPublications}
                        isLoading={arePublicationsLoading}
                        loadPublications={loadPublications}
                        salesforceId={salesforceId}
                        viewerPublication={viewerPublication}
                        setViewerPublication={setViewerPublication}
                        isPublicationCustomer={organization.is_publication_customer}
                        organization={organization}
                        setIsLoading={setArePublicationsLoading}
                    />
                </Grid>
            )}
            <Grid
                className={styles.mobileInlineAd}
                size={12}
                aria-hidden={!isExtraSmallScreen}
            >
                {themeContext === POL_THEME
                    ? renderAdSlot(organizationAds.SAF2_Listing_Sponsor_300x50_Mobile, 'mobile-org-listing-sponsor-ad-1')
                    : renderAdSlot(organizationAds.OSC2_Listing_Sponsor_300x50_Mobile, 'mobile-org-listing-sponsor-ad-1')}
            </Grid>
            {themeContext === POL_THEME && readings
                && (
                    <Grid size={12}>
                        <ReadingsSection
                            key={readings.id}
                            reading={readings}
                            org={organization}
                            listingId={salesforceId}
                            pageSlug={slug}
                            startOfWeek={getStartOfWeek()}
                        />
                    </Grid>
                )}
            {organization?.description && (
                <Grid size={12}>
                    <AboutUs
                        description={organization.description}
                    />
                </Grid>
            )}
            {hasActiveEvents(events) && (
                <Grid size={12}>
                    <Box ref={eventsRef}>
                        <Events events={events} isLoading={eventsLoading} />
                    </Box>
                </Grid>
            )}
            <Grid size={12}>
                <Videos
                    entityId={salesforceId}
                />
            </Grid>
            <Grid size={12}>
                <Gallery
                    photos={galleryPhotos}
                    loadPhotosData={loadGalleryPhotos}
                    listingId={salesforceId}
                />
            </Grid>
            {!isMediumScreen && organization.is_publication_customer
                && (
                    <Grid size={12}>
                        <AdsSponsorsSection
                            organizationSalesforceId={salesforceId}
                            slug={slug}
                        />
                    </Grid>
                )}
            {currentVariant === 'default' && organization && !canAdminClaimed()
                && (
                    <Grid size={12}>
                        <OwnershipRequest
                            listingId={salesforceId}
                            listingTitle={organization.physical_title}
                            listingType={LISTING_TYPE_ORGANIZATION}
                        />
                    </Grid>
                )}
        </Grid>
    );

    const rightSidebarContent = (
        <Grid
            className={classNames(styles.rightSideBarContainer, styles.alignContentStart, isBannerAdPresent && styles.topBannerPresent)}
            container
            spacing={2}
            aria-hidden={!isMediumScreen}
        >
            {organization && canAdminClaimed()
                && (
                    <Grid size={12}>
                        {adminMenuContent}
                    </Grid>
                )}
            <Grid
                className={classNames(styles.rightSideAdContainer, areFillerAdsSwapped && styles.swapAdUnits)}
                container
                size={12}
                spacing={2}
            >
                {themeContext === POL_THEME
                    ? rightColumnAdSlots.POL.map((ad, index) => {
                        const isFillerAd = ad.unit === 'SAF2_ListingInternal1_300x150';
                        const isCustomer = organization.is_publication_customer;
                        const hasPublications = sortedPublications.length > 0;
                        const isDefaultPage = currentVariant === 'default';

                        // Hide filler ad unit
                        const isHidingAdUnit = isFillerAd
                            && (
                                // when publications aren't present
                                !hasPublications
                                // or organization isn't a customer
                                || !isCustomer
                                // or user is not on main details page
                                || !isDefaultPage
                            );

                        return index === 0 ? (
                            <Grid
                                className={styles.adSlotContainer}
                                key={ad.unit}
                                size={12}
                            >
                                {renderAdSlot(ad, `right-column-ad-${index}`, swapAdsOnSlotRender)}
                            </Grid>
                        ) : (
                            <Grid
                                className={classNames(styles.adSlotContainer, isHidingAdUnit && styles.displayNone)}
                                key={ad.unit}
                                size={12}
                                sx={{
                                    order: index,
                                }}
                            >
                                {renderAdSlot(ad, `right-column-ad-${index}`)}
                            </Grid>
                        );
                    })
                    : rightColumnAdSlots.MCO.map((ad, index) => {
                        const isFillerAd = ad.unit === 'OSC2_ListingInternal1_300x150';
                        const isCustomer = organization.is_publication_customer;
                        const hasPublications = sortedPublications.length > 0;
                        const isDefaultPage = currentVariant === 'default';

                        // Hide filler ad unit
                        const isHidingAdUnit = isFillerAd
                            && (
                                // when publications aren't present
                                !hasPublications
                                // or organization isn't a customer
                                || !isCustomer
                                // or user is not on main details page
                                || !isDefaultPage
                            );

                        return index === 0 ? (
                            <Grid
                                className={styles.adSlotContainer}
                                key={ad.unit}
                                size={12}
                            >
                                {renderAdSlot(ad, `right-column-ad-${index}`, swapAdsOnSlotRender)}
                            </Grid>
                        ) : (
                            <Grid
                                className={classNames(styles.adSlotContainer, isHidingAdUnit && styles.displayNone)}
                                key={ad.unit}
                                size={12}
                                sx={{
                                    order: index,
                                }}
                            >
                                {renderAdSlot(ad, `right-column-ad-${index}`)}
                            </Grid>
                        );
                    })}
            </Grid>
            {organization.is_publication_customer && (
                <Grid size={12}>
                    <AdsSponsorsSection
                        organizationSalesforceId={salesforceId}
                        slug={slug}
                    />
                </Grid>
            )}
        </Grid>
    );

    return (
        <DFPSlotsProvider
            dfpNetworkId={process.env.DFP_NETWORK_ID}
            singleRequest
            disableInitialLoad
        >
            <main>
                {!organization && (
                    <Loader color={COLORS.secondary} />
                )}
                {salesforceId && (
                    <>
                        <PopupSubscribeForm
                            organizationTitle={organization.physical_title}
                            salesforceId={salesforceId}
                            isPopupOpen={isPopupOpen}
                            setIsPopupOpen={setIsPopupOpen}
                        />
                        <Box className={styles.pageContainer}>
                            {/* Skyscraper ad for mobile */}
                            <Box
                                className={styles.mobileAdSkyscraper}
                                aria-hidden={!isExtraSmallScreen}
                            >
                                {themeContext === POL_THEME
                                    ? renderAdSlot(organizationAds.SAF2_Listing_Sponsor_320x50_Mobile, 'mobile-org-skyscraper-ad')
                                    : renderAdSlot(organizationAds.OSC2_Listing_Sponsor_320x50_Mobile, 'mobile-org-skyscraper-ad')}
                            </Box>
                            {/* Left-side skyscraper ad for desktop */}
                            <Box
                                className={styles.leftSideBarContainer}
                                aria-hidden={isExtraSmallScreen}
                            >
                                <Box className={styles.desktopAdSkyscraper}>
                                    {themeContext === POL_THEME
                                        ? renderAdSlot(organizationAds.SAF2_Listing_Sponsor_160x600, 'desktop-org-skyscraper-ad')
                                        : renderAdSlot(organizationAds.OSC2_Listing_Sponsor_160x600, 'desktop-org-skyscraper-ad')}
                                </Box>
                            </Box>
                            {/* Primary details container */}
                            {primaryContent}
                            {/* Right-side ads container */}
                            {rightSidebarContent}
                        </Box>
                    </>
                )}
            </main>
        </DFPSlotsProvider>
    );
};

export default OrganizationDetailContainer;
